import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ScrollText,
  Loader2,
  CheckCircle2,
  Trash2,
  Eye,
  Send,
  Sparkles,
  Clock,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

function ScheduleDialog({ draft, onClose, onSaved, isAr }) {
  const [busy, setBusy] = useState(false);
  // Default to tomorrow 09:00 in local time.
  const defaultLocal = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    // Format for <input type="datetime-local"> (YYYY-MM-DDTHH:mm)
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [when, setWhen] = useState(defaultLocal);

  const submit = async () => {
    if (!draft || !when) return;
    setBusy(true);
    try {
      // Convert the local datetime-local value to an ISO UTC string.
      const iso = new Date(when).toISOString();
      await api.post(`/admin/blog/${draft.id}/schedule`, {
        publish_at: iso,
      });
      toast.success(isAr ? "تمت الجدولة" : "Scheduled");
      onSaved?.();
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        (isAr ? "فشل الجدولة" : "Schedule failed");
      toast.error(typeof msg === "string" ? msg : "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!draft} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="schedule-modal"
        dir={isAr ? "rtl" : "ltr"}
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isAr ? "جدولة نشر تلقائي" : "Schedule auto-publish"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 leading-relaxed">
            {isAr
              ? "حدّد التاريخ والوقت الذي تريد نشر المسوّدة فيه. سيتم نشرها تلقائياً عند حلول الموعد، ويمكنك إلغاء الجدولة في أي لحظة قبل ذلك."
              : "Pick the exact date & time. The article will go live automatically, and you can cancel the schedule anytime before it fires."}
          </DialogDescription>
        </DialogHeader>
        <div
          className="border border-slate-200 bg-slate-50 px-4 py-2 text-sm mt-2"
        >
          <p className="font-medium line-clamp-2" dir="rtl">{draft?.title}</p>
        </div>
        <div className="mt-2">
          <label className="text-xs uppercase tracking-[0.18em] text-slate-500 block mb-2">
            {isAr ? "تاريخ ووقت النشر" : "Publish at"}
          </label>
          <Input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            data-testid="schedule-datetime-input"
            className="rounded-sm h-11"
          />
        </div>
        <DialogFooter className="mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="schedule-cancel-btn"
          >
            {isAr ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy || !when}
            className="btn-gold rounded-sm"
            data-testid="schedule-confirm-btn"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 me-2 animate-spin" />
            ) : (
              <Clock className="w-4 h-4 me-2" />
            )}
            {isAr ? "جدولة" : "Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DraftPreview({ draft, onClose, isAr }) {
  return (
    <Dialog open={!!draft} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="draft-preview-modal"
        dir={isAr ? "rtl" : "ltr"}
        className="max-w-3xl max-h-[88vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl leading-tight">
            {draft?.title}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-3 space-y-3">
          <p className="text-xs text-slate-500 uppercase tracking-[0.18em]">
            {draft?.category} · {draft?.read_minutes}{" "}
            {isAr ? "دقيقة قراءة" : "min read"} · {draft?.author}
          </p>
          <p
            className="text-sm leading-relaxed text-slate-600 border-s-2 ps-3"
            style={{ borderColor: "var(--gold-deep)" }}
          >
            {draft?.excerpt}
          </p>
          <article
            className="prose prose-slate max-w-none text-base leading-relaxed whitespace-pre-line"
            dir="rtl"
          >
            {draft?.body}
          </article>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminBlogDrafts() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [drafts, setDrafts] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [previewing, setPreviewing] = useState(null);
  const [scheduling, setScheduling] = useState(null);

  const load = useCallback(() => {
    api
      .get("/admin/blog/drafts", { params: { limit: 100 } })
      .then((r) => setDrafts(r.data?.items || []))
      .catch(() => setDrafts([]));
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    load();
  }, [user, load]);

  const publish = async (d) => {
    setBusyId(d.id);
    try {
      await api.patch(`/admin/blog/${d.id}/publish`);
      toast.success(isAr ? "تم نشر المقال" : "Article published");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || (isAr ? "فشل النشر" : "Failed");
      toast.error(typeof msg === "string" ? msg : "Failed");
    } finally {
      setBusyId("");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(isAr ? "هل تريد حذف المسوّدة نهائياً؟" : "Delete this draft permanently?")) return;
    setBusyId(d.id);
    try {
      await api.delete(`/admin/blog/${d.id}`);
      toast.success(isAr ? "تم الحذف" : "Deleted");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || (isAr ? "فشل الحذف" : "Failed");
      toast.error(typeof msg === "string" ? msg : "Failed");
    } finally {
      setBusyId("");
    }
  };

  const unschedule = async (d) => {
    setBusyId(d.id);
    try {
      await api.post(`/admin/blog/${d.id}/unschedule`);
      toast.success(isAr ? "تم إلغاء الجدولة" : "Schedule cancelled");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || (isAr ? "فشل الإلغاء" : "Failed");
      toast.error(typeof msg === "string" ? msg : "Failed");
    } finally {
      setBusyId("");
    }
  };

  const fmtScheduled = (iso) => {
    try {
      return new Date(iso).toLocaleString(isAr ? "ar-KW" : "en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso || "";
    }
  };

  if (loading) {
    return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login?next=/admin/blog-drafts" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div
      data-testid="admin-blog-drafts-page"
      className="max-w-7xl mx-auto px-6 lg:px-10 py-20"
    >
      <Link
        to="/admin"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 transition-colors mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {isAr ? "← لوحة التحكم" : "← Dashboard"}
      </Link>

      <div className="grid lg:grid-cols-12 gap-12 mb-12">
        <div className="lg:col-span-7">
          <p className="eyebrow inline-flex items-center gap-2">
            <ScrollText className="w-3.5 h-3.5" style={{ color: "var(--gold-deep)" }} />
            {isAr ? "مسوّدات المدوّنة" : "Blog drafts"}
          </p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {isAr ? "راجع وانشر بضغطة." : "Review, then publish."}
          </h1>
        </div>
        <div className="lg:col-span-5 lg:pt-4">
          <p className="text-base text-slate-600 leading-relaxed">
            {isAr
              ? "كل مقال يُولَّد بالذكاء الاصطناعي من أفكار السوشل ميديا يصل هنا كمسوّدة. اقرأ، عدّل عند الحاجة، وانشره بضغطة على /blog."
              : "Every AI-generated article from your social ideas lands here as an unpublished draft. Review and click publish to push it live to /blog."}
          </p>
        </div>
      </div>

      {drafts === null ? (
        <div className="p-16 text-center text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
        </div>
      ) : drafts.length === 0 ? (
        <div
          data-testid="drafts-empty"
          className="border border-dashed border-slate-300 p-20 text-center text-slate-500"
        >
          <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: "var(--gold-deep)" }} />
          <p className="text-sm">
            {isAr
              ? "لا توجد مسوّدات حالياً. توجّه إلى صفحة «أفكار إنستغرام» وحوّل أي فكرة إلى مقال."
              : "No drafts yet. Head to the Instagram briefs page and convert any idea into an article."}
          </p>
          <Link to="/admin/social-topics" className="inline-block mt-4">
            <Button variant="outline" className="rounded-sm h-10">
              {isAr ? "افتح صفحة الأفكار" : "Open the briefs page"}
            </Button>
          </Link>
        </div>
      ) : (
        <ul
          data-testid="drafts-list"
          className="border border-slate-200 bg-white divide-y divide-slate-100"
        >
          {drafts.map((d) => (
            <li
              key={d.id}
              data-testid={`draft-row-${d.id}`}
              className="p-6 flex flex-col lg:flex-row lg:items-start gap-5 hover:bg-slate-50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {d.scheduled_at ? (
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 text-white inline-flex items-center gap-1"
                      style={{ background: "var(--navy, #0b1a2d)" }}
                      data-testid={`scheduled-badge-${d.id}`}
                    >
                      <Clock className="w-3 h-3" />
                      {isAr ? "مُجدولة" : "Scheduled"}
                    </span>
                  ) : (
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 text-white"
                      style={{ background: "var(--gold-deep)" }}
                    >
                      {isAr ? "مسوّدة" : "Draft"}
                    </span>
                  )}
                  <span className="text-[11px] text-slate-500 uppercase tracking-[0.15em]">
                    {d.category}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    · {d.read_minutes} {isAr ? "د قراءة" : "min"}
                  </span>
                  {d.source === "social_brief" && (
                    <span className="text-[10px] text-slate-400 uppercase">
                      · {isAr ? "من فكرة سوشل" : "from social brief"}
                    </span>
                  )}
                </div>
                <h3
                  className="font-display text-xl leading-snug tracking-tight"
                  dir="rtl"
                >
                  {d.title}
                </h3>
                {d.scheduled_at && (
                  <p
                    className="mt-1.5 text-xs"
                    style={{ color: "var(--gold-deep)" }}
                    data-testid={`scheduled-when-${d.id}`}
                  >
                    {isAr ? "ستُنشر تلقائياً في" : "Auto-publish at"}{" "}
                    <span dir="ltr">{fmtScheduled(d.scheduled_at)}</span>
                  </p>
                )}
                <p
                  className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3"
                  dir="rtl"
                >
                  {d.excerpt}
                </p>
                <p className="mt-3 text-xs text-slate-400" dir="rtl">
                  {isAr ? "بقلم" : "By"} {d.author}
                </p>
              </div>

              <div className="flex lg:flex-col gap-2 lg:w-44 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewing(d)}
                  data-testid={`preview-draft-${d.id}`}
                  className="rounded-sm h-10 flex-1"
                >
                  <Eye className="w-3.5 h-3.5 me-1.5" />
                  {isAr ? "معاينة" : "Preview"}
                </Button>
                {d.scheduled_at ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => unschedule(d)}
                    disabled={busyId === d.id}
                    data-testid={`unschedule-draft-${d.id}`}
                    className="rounded-sm h-10 flex-1"
                  >
                    {busyId === d.id ? (
                      <Loader2 className="w-3.5 h-3.5 me-1.5 animate-spin" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 me-1.5" />
                    )}
                    {isAr ? "إلغاء الجدولة" : "Unschedule"}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setScheduling(d)}
                    disabled={busyId === d.id}
                    data-testid={`schedule-draft-${d.id}`}
                    className="rounded-sm h-10 flex-1"
                  >
                    <Clock className="w-3.5 h-3.5 me-1.5" />
                    {isAr ? "جدولة" : "Schedule"}
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => publish(d)}
                  disabled={busyId === d.id}
                  data-testid={`publish-draft-${d.id}`}
                  className="btn-gold rounded-sm h-10 flex-1"
                >
                  {busyId === d.id ? (
                    <Loader2 className="w-3.5 h-3.5 me-1.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5 me-1.5" />
                  )}
                  {isAr ? "نشر الآن" : "Publish now"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(d)}
                  disabled={busyId === d.id}
                  data-testid={`delete-draft-${d.id}`}
                  className="rounded-sm h-10 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <DraftPreview
        draft={previewing}
        onClose={() => setPreviewing(null)}
        isAr={isAr}
      />
      <ScheduleDialog
        draft={scheduling}
        onClose={() => setScheduling(null)}
        onSaved={load}
        isAr={isAr}
      />

      {drafts && drafts.length > 0 && (
        <div className="mt-10 text-center">
          <Link
            to="/blog"
            data-testid="view-published-blog"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
          >
            <CheckCircle2 className="w-4 h-4" />
            {isAr ? "اطّلع على المقالات المنشورة في المدوّنة" : "View published articles on /blog"}
          </Link>
        </div>
      )}
    </div>
  );
}
